import { FC, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import $api from "../../shared/api/axios";
import { AppLoader } from "../../shared/components/AppLoader";
import { getRoutePromptsRevision } from "../../shared/const/router";
import { DeficiencyDialog } from "../../shared/components/DeficiencyDialog/DeficiencyDialog";
import { NewDraftDialog } from "./NewDraftDialog";
import { STATUS_STYLES, TRevisionSummary, formatTimestamp } from "./revision";

type TProps = { featureKey: string };

const PAGE_SIZE = 8;

export const RevisionHistory: FC<TProps> = ({ featureKey }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [newDraftOpen, setNewDraftOpen] = useState(false);
  const [openDeficiencyId, setOpenDeficiencyId] = useState<number | null>(null);

  const { data: revisions, isLoading, isError } = useQuery<TRevisionSummary[]>({
    queryKey: ["admin-prompt-revisions", featureKey],
    queryFn: async () => {
      const response = await $api.get(`/admin/prompts/features/${featureKey}/revisions`);
      return response?.data || [];
    }
  });

  const createDraft = useMutation({
    mutationFn: async (payload: { deficiencyId?: number; note?: string }) => {
      const body = payload.deficiencyId
        ? { deficiencyId: payload.deficiencyId, note: `Cure deficiency #${payload.deficiencyId}` }
        : { note: payload.note };
      const response = await $api.post(`/admin/prompts/features/${featureKey}/revisions`, body);
      return response.data as { revisionId: number; droppedKeys: string[] };
    },
    onSuccess: (data) => {
      if (data.droppedKeys.length > 0) {
        toast.warning(`Draft created; prompts no longer in code were dropped: ${data.droppedKeys.join(", ")}`);
      } else {
        toast.success(`Draft revision ${data.revisionId} created`);
      }
      setNewDraftOpen(false);
      queryClient.invalidateQueries({ queryKey: ["admin-prompt-revisions", featureKey] });
      navigate(getRoutePromptsRevision(featureKey, data.revisionId));
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      toast.error(error?.response?.data?.message ?? "Failed to create a draft")
  });

  const existingDraft = revisions?.find((revision) => revision.status === "draft");

  const renderList = () => {
    if (isLoading) return <AppLoader />;
    if (isError || !revisions) return <div className="py-6 text-center text-red-600">Failed to load revisions.</div>;
    if (revisions.length === 0) {
      return <div className="py-6 text-center text-gray-500">No revisions yet — prompts resolve from the code defaults.</div>;
    }

    const visible = revisions.slice(0, visibleCount);
    const remaining = revisions.length - visible.length;

    return (
      <>
      <div className="divide-y">
        {visible.map((revision) => (
          <Link
            key={revision.id}
            to={getRoutePromptsRevision(featureKey, revision.id)}
            className="flex items-start justify-between gap-4 px-6 py-3 hover:bg-gray-50"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <FigmaTypography component="span" variant="body-2" className="text-gray-900">
                  Revision {revision.id}
                </FigmaTypography>
                <span className={`rounded px-2 py-0.5 text-[12px] leading-[18px] ${STATUS_STYLES[revision.status]}`}>
                  {revision.status}
                </span>
                {revision.parentRevisionId && (
                  <FigmaTypography component="span" variant="body-3" className="text-gray-400">
                    from revision {revision.parentRevisionId}
                  </FigmaTypography>
                )}
                {revision.deficiencyId && (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      setOpenDeficiencyId(revision.deficiencyId);
                    }}
                    className="rounded bg-amber-50 px-2 py-0.5 text-[12px] leading-[18px] text-amber-700 hover:bg-amber-100"
                  >
                    deficiency #{revision.deficiencyId}
                  </button>
                )}
              </div>
              {revision.note && (
                <FigmaTypography component="div" variant="body-3" className="text-gray-500 mt-0.5 truncate">
                  {revision.note}
                </FigmaTypography>
              )}
            </div>
            <div className="shrink-0 text-right text-[12px] leading-[18px] text-gray-400">
              <div>{revision.entryCount} prompts</div>
              <div>
                {revision.status === "active" || revision.status === "inactive"
                  ? `activated ${formatTimestamp(revision.activatedAt)}`
                  : `created ${formatTimestamp(revision.createdAt)}`}
              </div>
              <div>{revision.activatedByName ?? revision.createdByName ?? "seeded from code"}</div>
            </div>
          </Link>
        ))}
      </div>
      {(remaining > 0 || visibleCount > PAGE_SIZE) && (
        <div className="flex items-center justify-center gap-4 border-t px-6 py-3">
          {remaining > 0 && (
            <button
              type="button"
              onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
              className="text-[13px] text-gray-600 hover:text-gray-900"
            >
              Show {Math.min(PAGE_SIZE, remaining)} more ({remaining} hidden)
            </button>
          )}
          {visibleCount > PAGE_SIZE && (
            <button
              type="button"
              onClick={() => setVisibleCount(PAGE_SIZE)}
              className="text-[13px] text-gray-400 hover:text-gray-700"
            >
              Collapse
            </button>
          )}
        </div>
      )}
      </>
    );
  };

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      <div className="flex items-center justify-between gap-4 px-6 py-4 border-b bg-gray-50">
        <FigmaTypography component="div" variant="body-2" className="text-gray-900">
          Revision history
        </FigmaTypography>
        {!existingDraft && (
          <AppButton appearance="outlined" onClick={() => setNewDraftOpen(true)}>
            New draft
          </AppButton>
        )}
      </div>
      {renderList()}
      <NewDraftDialog
        open={newDraftOpen}
        onClose={() => setNewDraftOpen(false)}
        onCreate={(payload) => createDraft.mutate(payload)}
        isCreating={createDraft.isPending}
      />
      <DeficiencyDialog deficiencyId={openDeficiencyId} onClose={() => setOpenDeficiencyId(null)} />
    </div>
  );
};
